(function () {
  const observedMediaColumns = new WeakSet();
  let quickViewDialog;
  let relatedProductsAlignmentFrame;

  const stripHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html || '';
    return template.content.textContent.trim();
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  };

  const getQuickViewDialog = () => {
    if (quickViewDialog) return quickViewDialog;

    quickViewDialog = document.createElement('dialog');
    quickViewDialog.className = 'bm-bundle-quick-view';
    quickViewDialog.innerHTML = `
      <div class="bm-bundle-quick-view__panel">
        <button class="bm-bundle-quick-view__close" type="button" aria-label="Stäng snabbvisning">×</button>
        <div class="bm-bundle-quick-view__content" aria-live="polite"></div>
      </div>
    `;

    quickViewDialog.addEventListener('click', (event) => {
      if (event.target === quickViewDialog || event.target.closest('.bm-bundle-quick-view__close')) {
        quickViewDialog.close();
      }
    });

    document.body.append(quickViewDialog);
    return quickViewDialog;
  };

  const openQuickView = async (productUrl, producer) => {
    const dialog = getQuickViewDialog();
    const content = dialog.querySelector('.bm-bundle-quick-view__content');
    const fullProductUrl = escapeHtml(productUrl);
    content.innerHTML = '<p class="bm-bundle-quick-view__loading">Laddar produktinformation...</p>';
    if (!dialog.open) dialog.showModal();

    try {
      const endpoint = new URL(productUrl, window.location.origin);
      endpoint.pathname = `${endpoint.pathname}.js`;

      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Product request failed');

      const product = await response.json();
      const description = escapeHtml(stripHtml(product.description));
      const image = product.featured_image
        ? `<img class="bm-bundle-quick-view__image" src="${escapeHtml(product.featured_image)}" alt="">`
        : '';
      const producerMeta = producer
        ? `<p class="bm-bundle-quick-view__meta"><strong>Producent:</strong> ${escapeHtml(producer)}</p>`
        : '';
      content.innerHTML = `
        ${image}
        <div class="bm-bundle-quick-view__body">
          <p class="bm-bundle-quick-view__eyebrow">I paketet ingår</p>
          <h2>${escapeHtml(product.title)}</h2>
          ${producerMeta ? `<div class="bm-bundle-quick-view__facts">${producerMeta}</div>` : ''}
          ${description ? `<p class="bm-bundle-quick-view__description">${description}</p>` : ''}
          <a class="bm-bundle-quick-view__link" href="${fullProductUrl}">Se hela produktsidan</a>
        </div>
      `;
    } catch (error) {
      content.innerHTML = `
        <div class="bm-bundle-quick-view__body">
          <p>Kunde inte ladda produktinformationen.</p>
          <a class="bm-bundle-quick-view__link" href="${fullProductUrl}">Se hela produktsidan</a>
        </div>
      `;
    }
  };

  const handleBundleQuickView = (event) => {
    const trigger = event.target.closest('.bm-bundle-quick-view__trigger, .bundle-components__card a');
    const productUrl = trigger?.dataset.productUrl;
    if (!productUrl) return;

    event.preventDefault();
    openQuickView(productUrl, trigger.dataset.productProducer);
  };

  const handleBundleQuickViewKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const trigger = event.target.closest('.bundle-components__card a');
    if (!trigger?.dataset.productUrl) return;

    event.preventDefault();
    openQuickView(trigger.dataset.productUrl, trigger.dataset.productProducer);
  };

  const prepareBundleQuickViewButtons = (bundleComponents) => {
    for (const card of bundleComponents.querySelectorAll('.bundle-components__card')) {
      if (card.querySelector('.bm-bundle-quick-view__trigger')) continue;

      const productLink = card.querySelector('a');
      const readMore = productLink?.querySelector('span');
      if (!productLink || !readMore) continue;

      const productUrl = productLink.href;
      productLink.dataset.productUrl = productUrl;
      productLink.removeAttribute('href');
      productLink.setAttribute('role', 'button');
      productLink.setAttribute('aria-haspopup', 'dialog');
      productLink.tabIndex = 0;

      const trigger = document.createElement('button');
      trigger.className = 'bm-bundle-quick-view__trigger';
      trigger.type = 'button';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.dataset.productUrl = productUrl;
      trigger.dataset.productProducer = productLink.dataset.productProducer || '';
      trigger.textContent = readMore.textContent;
      readMore.remove();
      card.append(trigger);
    }
  };

  const updateBundleSpacing = (mediaColumn, bundleComponents) => {
    const carouselFrame = mediaColumn.querySelector('.bm-pdp-carousel-frame');
    if (!carouselFrame) return;

    const mediaStack = mediaColumn.parentElement;
    const stackRect = mediaStack.getBoundingClientRect();
    const frameRect = carouselFrame.getBoundingClientRect();
    bundleComponents.style.setProperty('--bm-pdp-bundle-width', `${Math.round(frameRect.width)}px`);
    bundleComponents.style.setProperty('--bm-pdp-bundle-offset', `${Math.round(frameRect.left - stackRect.left)}px`);

  };

  const observeBundleSpacing = (mediaColumn, bundleComponents) => {
    updateBundleSpacing(mediaColumn, bundleComponents);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => updateBundleSpacing(mediaColumn, bundleComponents));
    });

    if (observedMediaColumns.has(mediaColumn)) return;

    if (!('ResizeObserver' in window)) return;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => updateBundleSpacing(mediaColumn, bundleComponents));
    });

    observer.observe(mediaColumn);

    const carouselFrame = mediaColumn.querySelector('.bm-pdp-carousel-frame');
    if (carouselFrame) {
      observer.observe(carouselFrame);

      const visibleImage = carouselFrame.querySelector('.product-media__image');
      if (visibleImage) observer.observe(visibleImage);
    }

    observedMediaColumns.add(mediaColumn);
  };

  const setViewportAlignedWidth = (element, left, right) => {
    if (!element) return;

    const width = Math.round(right - left);

    element.style.removeProperty('left');
    element.style.setProperty('position', 'relative', 'important');
    element.style.setProperty('transform', 'none', 'important');
    element.style.setProperty('width', `${width}px`, 'important');
    element.style.setProperty('max-width', `${width}px`, 'important');
    element.style.setProperty('margin-left', '0px', 'important');
    element.style.setProperty('margin-right', '0px', 'important');

    const renderedLeft = element.getBoundingClientRect().left;
    element.style.setProperty('left', `${Math.round(left - renderedLeft)}px`, 'important');
  };

  const centerPdpHero = (main) => {
    const productGrid = main.querySelector('.product-information__grid');
    if (!productGrid) return;

    productGrid.style.removeProperty('left');

    const viewportWidth = document.documentElement.clientWidth;
    if (viewportWidth < 750) {
      productGrid.style.removeProperty('position');
      return;
    }

    const visibleFrame =
      main.querySelector('.product-information__media .bm-pdp-carousel-frame') ||
      main.querySelector('.product-information__media .bm-product-media-image-frame');
    const detailsContent =
      main.querySelector('.product-details > .group-block') || main.querySelector('.product-details');

    if (!visibleFrame || !detailsContent) return;

    const frameRect = visibleFrame.getBoundingClientRect();
    const detailsRect = detailsContent.getBoundingClientRect();
    const heroCenter = (frameRect.left + detailsRect.right) / 2;
    const offset = Math.round(viewportWidth / 2 - heroCenter);

    productGrid.style.setProperty('position', 'relative', 'important');
    productGrid.style.setProperty('left', `${offset}px`, 'important');
  };

  const alignRelatedProducts = () => {
    const main = document.querySelector('main[data-template^="product"]');
    if (!main) return;

    centerPdpHero(main);

    const heading = main.querySelector('.bm-related-heading-bar__inner');
    const recommendations = main.querySelector(
      'product-recommendations[data-testid="product-recommendations-section"]'
    );
    const section = recommendations?.querySelector('.section');
    const sectionContent = section?.querySelector('.section-resource-list__content');
    const grid = recommendations?.querySelector('.resource-list.resource-list--grid');

    if (!heading || !recommendations || !section || !grid) return;

    const contentBlocks = sectionContent ? Array.from(sectionContent.children) : [];
    const containsOnlyStyleBlocks =
      contentBlocks.length > 0 &&
      contentBlocks.every((block) => {
        const childElements = Array.from(block.children);
        return childElements.length > 0 && childElements.every((child) => child.tagName === 'STYLE');
      });

    if (containsOnlyStyleBlocks) {
      sectionContent.style.setProperty('display', 'none', 'important');
    }

    const viewportWidth = document.documentElement.clientWidth;
    const edgePadding = viewportWidth < 750 ? 20 : 40;
    let left = edgePadding;
    let right = viewportWidth - edgePadding;

    if (viewportWidth >= 750) {
      const visibleFrame =
        main.querySelector('.product-information__media .bm-pdp-carousel-frame') ||
        main.querySelector('.bm-pdp-media-stack > .bundle-components') ||
        main.querySelector('.product-information__media .bm-product-media-image-frame');
      const buyButtons = main.querySelector('.product-details .buy-buttons-block');

      if (!visibleFrame || !buyButtons) return;

      left = Math.max(visibleFrame.getBoundingClientRect().left, edgePadding);
      right = Math.min(buyButtons.getBoundingClientRect().right, viewportWidth - edgePadding);
    }

    if (right <= left) return;

    setViewportAlignedWidth(heading, left, right);
    heading.style.setProperty('padding-left', '0px', 'important');
    heading.style.setProperty('padding-right', '0px', 'important');
    setViewportAlignedWidth(recommendations, left, right);
    section.style.setProperty('width', '100%', 'important');
    section.style.setProperty('max-width', '100%', 'important');
    section.style.setProperty('margin-left', '0px', 'important');
    section.style.setProperty('margin-right', '0px', 'important');
    section.style.setProperty('padding-left', '0px', 'important');
    section.style.setProperty('padding-right', '0px', 'important');
    grid.style.setProperty('width', '100%', 'important');
    grid.style.setProperty('max-width', '100%', 'important');
    grid.style.setProperty('margin-left', '0px', 'important');
    grid.style.setProperty('margin-right', '0px', 'important');
    grid.style.setProperty('grid-column', '1 / -1', 'important');
    grid.style.setProperty(
      'grid-template-columns',
      `repeat(${viewportWidth < 990 ? 2 : 4}, minmax(0, 1fr))`,
      'important'
    );
    grid.style.setProperty('justify-content', 'stretch', 'important');
    grid.style.setProperty('column-gap', '20px', 'important');

    for (const card of grid.querySelectorAll('.resource-list__item, product-card')) {
      card.style.setProperty('width', '100%', 'important');
      card.style.setProperty('max-width', '100%', 'important');
    }
  };

  const scheduleRelatedProductsAlignment = () => {
    window.cancelAnimationFrame(relatedProductsAlignmentFrame);
    relatedProductsAlignmentFrame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(alignRelatedProducts);
    });
  };

  const moveBundleComponentsBelowMedia = () => {
    const main = document.querySelector('main[data-template^="product"]');
    if (!main) return;

    for (const productInformation of main.querySelectorAll('.product-information')) {
      const mediaColumn = productInformation.querySelector('.product-information__media');
      const bundleComponents = productInformation.querySelector('.bundle-components');
      let mediaStack = productInformation.querySelector('.bm-pdp-media-stack');

      if (!mediaColumn || !bundleComponents) continue;

      if (!mediaStack) {
        mediaStack = document.createElement('div');
        mediaStack.className = 'bm-pdp-media-stack';
        mediaColumn.before(mediaStack);
        mediaStack.append(mediaColumn);
      }

      if (bundleComponents.parentElement !== mediaStack) {
        mediaStack.append(bundleComponents);
      }

      prepareBundleQuickViewButtons(bundleComponents);
      observeBundleSpacing(mediaColumn, bundleComponents);
    }

    scheduleRelatedProductsAlignment();
  };

  const observeProductPage = () => {
    moveBundleComponentsBelowMedia();

    const main = document.querySelector('main[data-template^="product"]');
    if (!main) return;

    const observer = new MutationObserver(moveBundleComponentsBelowMedia);
    observer.observe(main, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeProductPage, { once: true });
  } else {
    observeProductPage();
  }

  document.addEventListener('shopify:section:load', moveBundleComponentsBelowMedia);
  window.addEventListener('resize', moveBundleComponentsBelowMedia);
  document.addEventListener('click', handleBundleQuickView);
  document.addEventListener('keydown', handleBundleQuickViewKeydown);
})();
